// Akıllı WhatsApp linki (v3):
// • Masaüstünde `web.whatsapp.com/send?phone=...&text=...`
//   → WA Web'i direkt açar, mesajı sohbet kutusuna doldurur.
// • Mobilde `wa.me/{phone}?text=...`
//   → yüklü uygulamayı deep-link ile açar.
//
// Sağlamlaştırma:
// • Telefon: sadece rakam bırakılır, + / boşluk / parantez / tire hepsi silinir.
// • Metin: HER ZAMAN `encodeURIComponent`'ten geçer → boşluk %20, Türkçe
//   harfler (İ/Ş/Ç/ı/ü/ö/ğ), newline (\n) ve ₺ / emoji hepsi güvenli.
// • Fallback: mesaj panoya kopyalanır → WA Web geç yüklenip mesajı gösteremezse
//   toast içindeki "WA Aç" butonu ile manuel tetikleme + Ctrl+V.

/** Sadece rakam bırak — "+90 (534) 964-67 03" → "905349646703". */
const cleanPhone = (raw) => String(raw || "").replace(/\D/g, "");

/**
 * Türkçe karakter, boşluk, newline, ₺ ve emoji için %100 güvenli kodlama.
 * Çift-encode olmasın diye önce decode denemesi yapılmaz — bilinçli bir tercih.
 */
const safeEncode = (text) => encodeURIComponent(String(text ?? ""));

export const isMobileUA = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /Android|iPhone|iPad|iPod|IEMobile|BlackBerry|Opera Mini|Mobile|Silk/i.test(ua);
};

/**
 * Platforma göre stabil WhatsApp URL'si üret.
 * @param {string} phone - +90 / boşluk / parantez tolerant, ülke kodu şart
 * @param {string} text  - düz metin, burada encode edilir
 * @param {{ forceWeb?: boolean }} opts
 *
 * NOT: PC'de `api.whatsapp.com/send` — WhatsApp'ın resmi
 * yönlendirici sayfasıdır; sekmeyi web.whatsapp.com veya masaüstü
 * uygulamasına stabil yönlendirir. `web.whatsapp.com/send` bazı
 * tarayıcılarda "please use mobile" hatası veriyordu.
 */
export function buildWhatsAppUrl(phone, text, opts = {}) {
  const p = cleanPhone(phone);
  const encoded = safeEncode(text);
  const useWeb = opts.forceWeb || !isMobileUA();
  return useWeb
    ? `https://api.whatsapp.com/send?phone=${p}&text=${encoded}`
    : `https://wa.me/${p}?text=${encoded}`;
}

/** Alternatif yönlendirme (fallback) — api.whatsapp.com handoff sayfası. */
export function buildWhatsAppApiUrl(phone, text) {
  const p = cleanPhone(phone);
  return `https://api.whatsapp.com/send?phone=${p}&text=${safeEncode(text)}`;
}

/** Best-effort copy — never throws; returns true on success. */
export async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(text ?? ""));
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = String(text ?? "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch { return false; }
}

/**
 * Fire-and-forget WhatsApp open.
 * 1. Mesajı önce panoya kopyalar (fallback için).
 * 2. Stabil web.whatsapp.com/send (PC) veya wa.me (mobil) URL'sini yeni sekmede açar.
 * Returns { url, copied, fallbackUrl }.
 */
export async function openWhatsApp(phone, text, opts = {}) {
  const url = buildWhatsAppUrl(phone, text, opts);
  const fallbackUrl = buildWhatsAppApiUrl(phone, text);
  const copied = await copyToClipboard(text);
  window.open(url, "_blank", "noopener,noreferrer");
  return { url, copied, fallbackUrl };
}
